/**
 * Revolut Merchant Checkout — popup helper.
 *
 * Loads the official embed.js once per page, then opens the checkout in a
 * modal overlay on top of our UI. No tabs, no redirects — the user stays
 * in the Sense Billing page and we close our TopUpDialog on success.
 *
 * Embed URLs:
 *   - production: https://merchant.revolut.com/embed.js
 *   - sandbox:    https://sandbox-merchant.revolut.com/embed.js
 */

declare global {
  interface Window {
    RevolutCheckout?: (token: string, env?: 'prod' | 'sandbox') => Promise<RevolutCheckoutInstance>;
  }
}

interface RevolutCheckoutInstance {
  payWithPopup: (opts: {
    /** Save the card during this checkout — 'merchant' enables off-session
     *  MIT charges later (auto-topup); 'customer' only allows on-session
     *  reuse on Revolut checkout pages. Per Revolut docs this is the
     *  ONLY way to save a card — backend save_payment_method_for is
     *  ignored. */
    savePaymentMethodFor?: 'customer' | 'merchant';
    onSuccess?: () => void;
    onError?: (error: { message: string }) => void;
    onCancel?: () => void;
  }) => void;
  destroy?: () => void;
}

const SCRIPT_URLS = {
  production: 'https://merchant.revolut.com/embed.js',
  sandbox: 'https://sandbox-merchant.revolut.com/embed.js',
};

const loaded: Record<string, Promise<void>> = {};

function loadScript(env: 'production' | 'sandbox'): Promise<void> {
  if (env in loaded) return loaded[env];
  loaded[env] = new Promise((resolve, reject) => {
    const src = SCRIPT_URLS[env];
    const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
    if (existing) {
      if ((existing as any).__loaded) { resolve(); return; }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Revolut embed.js')));
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => { (s as any).__loaded = true; resolve(); };
    s.onerror = () => reject(new Error('Failed to load Revolut embed.js'));
    document.head.appendChild(s);
  });
  return loaded[env];
}

export interface OpenCheckoutOpts {
  token: string;
  environment: 'production' | 'sandbox';
  /** Tells Revolut to tokenise the card on this checkout. 'merchant' is what
   *  unlocks later off-session MIT charges. Without this the card is one-off. */
  savePaymentMethodFor?: 'customer' | 'merchant';
  onSuccess: () => void;
  onError: (msg: string) => void;
  onCancel?: () => void;
}

export async function openRevolutCheckout(opts: OpenCheckoutOpts): Promise<void> {
  await loadScript(opts.environment);
  if (!window.RevolutCheckout) {
    opts.onError('Revolut Checkout SDK not available');
    return;
  }
  const instance = await window.RevolutCheckout(
    opts.token,
    opts.environment === 'sandbox' ? 'sandbox' : 'prod',
  );
  instance.payWithPopup({
    ...(opts.savePaymentMethodFor ? { savePaymentMethodFor: opts.savePaymentMethodFor } : {}),
    onSuccess: opts.onSuccess,
    onError: (e) => opts.onError(e?.message || 'Payment failed'),
    onCancel: opts.onCancel,
  });
}
